import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import VoiceDictationInput from '../../components/VoiceDictationInput';
import SignatureInput from '../../components/SignatureInput';
import { FileText, Save, ArrowLeft, Download, Printer } from 'lucide-react';
import { exportFillableFromDom } from '../../utils/fillable';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ComprehensiveNursesNotes = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const formId = searchParams.get('id');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState([]);
  
  const [formData, setFormData] = useState({
    // Patient Info
    patientId: '',
    patientsName: '',
    medicalRecordNumber: '',
    
    // OBSERVATION Section
    allergies: '',
    medicationChangeSinceLastVisit: '',
    medicationChangeSpecify: '',
    homeboundNo: false,
    homeboundYes: false,
    homeboundReason: '',
    
    // VITAL SIGNS Section
    tempNoDeficit: false,
    temp: '',
    pulseNoDeficit: false,
    pulse: '',
    respNoDeficit: false,
    resp: '',
    wtNoDeficit: false,
    wt: '',
    bpNoDeficit: false,
    bpRight: '',
    bpLeft: '',
    vitalComments: '',
    extremityPulsesNoDeficit: false,
    extremityPulses: '',
    glucometer: '',
    bs: '',
    universalPrecautions: false,
    precautionsMaintained: false,
    
    // RESPIRATORY Section
    raleRhonchiNoDeficit: false,
    raleRhonchi: '',
    sobNoDeficit: false,
    sob: '',
    coughNoDeficit: false,
    cough: '',
    sputumNoDeficit: false,
    sputum: '',
    o2At: '',
    o2Sat: '',
    respiratoryOther: '',
    
    // SKIN Section
    edemaLocation: '',
    edemaTR: false,
    edema1Plus: false,
    edema2Plus: false,
    edema3Plus: false,
    edema4Plus: false,
    nonPitting: false,
    pitting: false,
    skinNoDeficit: false,
    warmDry: false,
    coolClammy: false,
    turgorAdequate: false,
    wound1Location: '',
    wound2Location: '',
    wound1L: '',
    wound1W: '',
    wound1D: '',
    wound1DrainageAmt: '',
    wound1DrainageColor: '',
    wound1DrainageOdor: '',
    wound2L: '',
    wound2W: '',
    wound2D: '',
    wound2DrainageAmt: '',
    wound2DrainageColor: '',
    wound2DrainageOdor: '',
    
    // GU Section
    guNoDeficit: false,
    distention: false,
    retention: false,
    burning: false,
    frequency: false,
    foleyCath: false,
    suprapubic: false,
    incontinence: false,
    catheterSize: '',
    catheterF: '',
    catheterMl: '',
    guComments: '',
    
    // CARDIOVASCULAR Section
    cvNoDeficit: false,
    chestPain: false,
    heartSounds: false,
    peripheralPulses: false,
    dizziness: false,
    edema: false,
    neckVeinDistention: false,
    arrhythmia: false,
    cvComments: '',
    
    // MUSCULOSKELETAL Section
    msNoDeficit: false,
    weakness: false,
    balanceGaitAbnormal: false,
    limitedMobilityROM: false,
    pain: false,
    gripStrength: '',
    gripStrengthRight: '',
    gripStrengthLeft: '',
    bedbound: false,
    chairbound: false,
    contracture: false,
    paralysis: false,
    assistiveDevice: false,
    fallPrecautionsMaintained: false,
    
    // NEUROLOGICAL Section
    neuroNoDeficit: false,
    orientedToPerson: false,
    orientedToPlace: false,
    orientedToTime: false,
    seizureTremors: false,
    pupillaryReaction: '',
    pupillaryRightLeftEqual: '',
    hearingImpaired: false,
    speechImpaired: false,
    visuallyImpaired: false,
    legallyBlind: false,
    
    // DIGESTIVE/NUTRITION Section
    digestiveNoDeficit: false,
    lastBM: '',
    nv: false,
    diarrhea: false,
    constipation: false,
    tubeFeeding: false,
    npoTypeAmount: '',
    placement: false,
    residualAmt: '',
    bowelSoundsPresent: false,
    abdGirth: '',
    diet: '',
    mealsPreparedAdministered: false,
    appropriately: false,
    past24HourDietRecallAdequate: false,
    past24HourDietRecallInadequate: false,
    
    // PAIN Section
    painFrequency: '',
    painFrequency0: false,
    painFrequency1: false,
    painFrequency2: false,
    painFrequency3: false,
    painPrimarySite: '',
    painIntensity: '',
    painIntensity0: false,
    painIntensity1: false,
    painIntensity2: false,
    painIntensity3: false,
    painIntensity4: false,
    painIntensity5: false,
    painIntensity6: false,
    painIntensity7: false,
    painIntensity8: false,
    painIntensity9: false,
    painIntensity10: false,
    currentPainManagement: '',
    painManagementTeaching: '',
    patientsPainGoal: '',
    progressTowardPainGoal: '',
    
    // DIAGNOSIS ICD-10 CODES
    diagnosisIcd10Codes: '',
    
    // SUPERVISION Section
    supervisionLPN: false,
    supervisionAide: false,
    presentOnThisVisitYes: false,
    presentOnThisVisitNo: false,
    aideFollowingCarePlanYes: false,
    aideFollowingCarePlanNo: false,
    courteousAndPoliteYes: false,
    courteousAndPoliteNo: false,
    reportChangesInStatusYes: false,
    reportChangesInStatusNo: false,
    patientSatisfiedWithCareYes: false,
    patientSatisfiedWithCareNo: false,
    changesMadeToCarePlanYes: false,
    changesMadeToCarePlanNo: false,
    additionalInstructionGivenYes: false,
    additionalInstructionGivenNo: false,
    
    // GOALS/PLANS Section
    goalsPlans: '',
    
    // PROGRESS/BARRIERS Section
    progressBarriers: '',
    
    // INTERVENTIONS Section
    progressTowardGoals: '',
    teachingToolsUsedGiven: '',
    instructed: false,
    ptCgVerbalizedUnderstanding: false,
    ptCgReturnDemonstration: false,
    conferenceWith: '',
    conferenceWithName: '',
    regarding: '',
    
    // CASE MANAGEMENT Section
    caseManagementConferenceWith: '',
    
    // Signatures (comprehensive - typed, voice recording, handwritten)
    patientSignature: { typed: '', audioURL: null, canvasDataURL: null },
    patientSignatureDate: '',
    nurseSignature: { typed: '', audioURL: null, canvasDataURL: null },
    nurseSignatureDate: '',
  });

  useEffect(() => {
    fetchPatients();
    if (formId) fetchForm();
  }, [formId]);

  const getToken = () => {
    const user = localStorage.getItem('ivory_user');
    if (user) {
      const userData = JSON.parse(user);
      return userData.token;
    }
    return localStorage.getItem('token');
  };

  const fetchPatients = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/patients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setPatients(data.patients);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const fetchForm = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/skilled-nursing/nurses-notes/${formId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.form) {
        setFormData(prev => ({ ...prev, ...data.form.formData }));
      }
    } catch (error) {
      console.error('Error fetching form:', error);
      toast.error('Failed to load form');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.patientId) {
      toast.error('Please select a patient');
      return;
    }
    
    setSaving(true);
    try {
      const token = getToken();
      const url = formId 
        ? `${API_URL}/api/skilled-nursing/nurses-notes/${formId}`
        : `${API_URL}/api/skilled-nursing/nurses-notes`;
      
      const selectedPatient = patients.find(p => p.id === formData.patientId);
      
      const payload = {
        patientId: formData.patientId,
        patientName: selectedPatient ? `${selectedPatient.firstName} ${selectedPatient.lastName}` : formData.patientsName,
        formType: 'comprehensive-nurses-notes',
        formData: formData
      };
      
      const response = await fetch(url, {
        method: formId ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        toast.success(formId ? 'Form updated' : 'Form created');
        if (!formId && data.form?.id) {
          navigate(`/dashboard/skilled-nursing/nurses-notes?id=${data.form.id}`);
        }
      } else {
        toast.error('Failed to save form');
      }
    } catch (error) {
      console.error('Error saving form:', error);
      toast.error('Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  // Helper component for checkbox with label
  const CheckboxField = ({ label, checked, onChange, className = '' }) => (
    <label className={`flex items-center gap-2 cursor-pointer ${className}`}>
      <input 
        type="checkbox" 
        checked={checked || false} 
        onChange={(e) => onChange(e.target.checked)} 
        className="w-4 h-4 rounded border-gray-300"
      />
      <span className="text-sm">{label}</span>
    </label>
  );

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate(-1)} className="p-2">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <FileText className="w-8 h-8 text-[#f39c12]" />
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Daily Nursing Notes</h1>
                <p className="text-sm text-gray-500">Ivory Home Health Care, Inc.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => exportFillableFromDom(document.getElementById('main-content'), 'Nurses_Notes.pdf')}>
                <Printer className="w-4 h-4 mr-2" /> Print
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#f39c12] hover:bg-[#e67e22]">
                <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-lg shadow-sm border p-6 space-y-6">
          {/* Company Header */}
          <div className="text-center border-b pb-4">
            <h2 className="text-xl font-bold text-[#f39c12]">Ivory Home Health Care. Inc.</h2>
            <p className="text-xs text-gray-600">Ph: (562) 349 - 0860; fax: (562) 285 – 7216</p>
            <p className="text-xs text-gray-600">Email: office@ivory_hhc.com; ivory_hhc@qbhm.net</p>
            <p className="text-xs text-gray-600">WEBSITE: www.ivory_hhc.com or www.ivory_hhc.net</p>
            <h3 className="text-lg font-bold mt-2">Daily Nursing Notes</h3>
          </div>

          {/* Patient Info */}
          <div className="grid grid-cols-2 gap-4 border-b pb-4">
            <div>
              <Label className="font-bold">Patient's Name</Label>
              <select
                value={formData.patientId}
                onChange={(e) => {
                  const patient = patients.find(p => p.id === e.target.value);
                  handleInputChange('patientId', e.target.value);
                  if (patient) handleInputChange('patientsName', `${patient.firstName} ${patient.lastName}`);
                }}
                className="w-full mt-1 px-3 py-2 border rounded-md"
              >
                <option value="">Select Patient...</option>
                {patients.map(p => (
                  <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
                ))}
              </select>
            </div>
            <VoiceDictationInput 
              label="Medical Record Number" 
              value={formData.medicalRecordNumber} 
              onChange={(e) => handleInputChange('medicalRecordNumber', e.target.value)} 
            />
          </div>

          {/* OBSERVATION Section */}
          <div className="border rounded-lg p-4 bg-blue-50">
            <h4 className="font-bold text-lg mb-3 text-blue-800">OBSERVATION</h4>
            <div className="space-y-3">
              <VoiceDictationInput 
                label="Allergies:" 
                value={formData.allergies} 
                onChange={(e) => handleInputChange('allergies', e.target.value)} 
              />
              <div className="flex items-center gap-4">
                <Label>Medication change since last visit?</Label>
                <CheckboxField label="No" checked={formData.medicationChangeSinceLastVisit === 'No'} onChange={() => handleInputChange('medicationChangeSinceLastVisit', 'No')} />
                <CheckboxField label="Yes, Specify" checked={formData.medicationChangeSinceLastVisit === 'Yes'} onChange={() => handleInputChange('medicationChangeSinceLastVisit', 'Yes')} />
              </div>
              {formData.medicationChangeSinceLastVisit === 'Yes' && (
                <VoiceDictationInput 
                  value={formData.medicationChangeSpecify} 
                  onChange={(e) => handleInputChange('medicationChangeSpecify', e.target.value)}
                  placeholder="Specify changes..."
                />
              )}
              <div className="flex items-center gap-4">
                <Label>Homebound?</Label>
                <CheckboxField label="No" checked={formData.homeboundNo} onChange={(v) => { handleInputChange('homeboundNo', v); if(v) handleInputChange('homeboundYes', false); }} />
                <CheckboxField label="Yes (If yes, reason)" checked={formData.homeboundYes} onChange={(v) => { handleInputChange('homeboundYes', v); if(v) handleInputChange('homeboundNo', false); }} />
              </div>
              {formData.homeboundYes && (
                <VoiceDictationInput 
                  value={formData.homeboundReason} 
                  onChange={(e) => handleInputChange('homeboundReason', e.target.value)}
                  placeholder="Reason for homebound status..."
                />
              )}
            </div>
          </div>

          {/* VITAL SIGNS Section */}
          <div className="border rounded-lg p-4 bg-green-50">
            <h4 className="font-bold text-lg mb-3 text-green-800">VITAL SIGNS</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Label>Temp:</Label>
                  <CheckboxField label="No Deficit" checked={formData.tempNoDeficit} onChange={(v) => handleInputChange('tempNoDeficit', v)} />
                </div>
                <VoiceDictationInput value={formData.temp} onChange={(e) => handleInputChange('temp', e.target.value)} placeholder="°F" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Label>Pulse:</Label>
                  <CheckboxField label="No Deficit" checked={formData.pulseNoDeficit} onChange={(v) => handleInputChange('pulseNoDeficit', v)} />
                </div>
                <VoiceDictationInput value={formData.pulse} onChange={(e) => handleInputChange('pulse', e.target.value)} placeholder="bpm" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Label>Resp:</Label>
                  <CheckboxField label="No Deficit" checked={formData.respNoDeficit} onChange={(v) => handleInputChange('respNoDeficit', v)} />
                </div>
                <VoiceDictationInput value={formData.resp} onChange={(e) => handleInputChange('resp', e.target.value)} placeholder="/min" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Label>Wt:</Label>
                  <CheckboxField label="No Deficit" checked={formData.wtNoDeficit} onChange={(v) => handleInputChange('wtNoDeficit', v)} />
                </div>
                <VoiceDictationInput value={formData.wt} onChange={(e) => handleInputChange('wt', e.target.value)} placeholder="lbs" />
              </div>
              <div className="col-span-2">
                <div className="flex items-center gap-2 mb-1">
                  <Label>BP:</Label>
                  <CheckboxField label="No Deficit" checked={formData.bpNoDeficit} onChange={(v) => handleInputChange('bpNoDeficit', v)} />
                </div>
                <div className="flex gap-2">
                  <VoiceDictationInput value={formData.bpRight} onChange={(e) => handleInputChange('bpRight', e.target.value)} placeholder="right" />
                  <VoiceDictationInput value={formData.bpLeft} onChange={(e) => handleInputChange('bpLeft', e.target.value)} placeholder="left" />
                </div>
              </div>
              <div className="col-span-2">
                <Label>Comments:</Label>
                <VoiceDictationInput value={formData.vitalComments} onChange={(e) => handleInputChange('vitalComments', e.target.value)} />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Label>Extremity Pulses</Label>
                  <CheckboxField label="No Deficit" checked={formData.extremityPulsesNoDeficit} onChange={(v) => handleInputChange('extremityPulsesNoDeficit', v)} />
                </div>
                <VoiceDictationInput value={formData.extremityPulses} onChange={(e) => handleInputChange('extremityPulses', e.target.value)} />
              </div>
              <div>
                <Label>Glucometer</Label>
                <VoiceDictationInput value={formData.glucometer} onChange={(e) => handleInputChange('glucometer', e.target.value)} />
              </div>
              <div>
                <Label>BS:</Label>
                <VoiceDictationInput value={formData.bs} onChange={(e) => handleInputChange('bs', e.target.value)} />
              </div>
              <div className="flex items-center gap-4">
                <CheckboxField label="Universal" checked={formData.universalPrecautions} onChange={(v) => handleInputChange('universalPrecautions', v)} />
                <CheckboxField label="Precautions" checked={formData.precautionsMaintained} onChange={(v) => handleInputChange('precautionsMaintained', v)} />
                <CheckboxField label="Maintained" checked={formData.precautionsMaintained} onChange={(v) => handleInputChange('precautionsMaintained', v)} />
              </div>
            </div>
          </div>

          {/* RESPIRATORY Section */}
          <div className="border rounded-lg p-4 bg-purple-50">
            <h4 className="font-bold text-lg mb-3 text-purple-800">RESPIRATORY</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                <Label>Rale/Rhonchi</Label>
                <CheckboxField label="No Deficit" checked={formData.raleRhonchiNoDeficit} onChange={(v) => handleInputChange('raleRhonchiNoDeficit', v)} />
              </div>
              <div className="flex items-center gap-2">
                <Label>SOB</Label>
                <CheckboxField label="No Deficit" checked={formData.sobNoDeficit} onChange={(v) => handleInputChange('sobNoDeficit', v)} />
              </div>
              <div className="flex items-center gap-2">
                <Label>Cough</Label>
                <CheckboxField label="No Deficit" checked={formData.coughNoDeficit} onChange={(v) => handleInputChange('coughNoDeficit', v)} />
              </div>
              <div className="flex items-center gap-2">
                <Label>Sputum</Label>
                <CheckboxField label="No Deficit" checked={formData.sputumNoDeficit} onChange={(v) => handleInputChange('sputumNoDeficit', v)} />
              </div>
              <VoiceDictationInput label="O2 at" value={formData.o2At} onChange={(e) => handleInputChange('o2At', e.target.value)} />
              <VoiceDictationInput label="O2Sat" value={formData.o2Sat} onChange={(e) => handleInputChange('o2Sat', e.target.value)} />
              <div className="col-span-2">
                <VoiceDictationInput label="Other" value={formData.respiratoryOther} onChange={(e) => handleInputChange('respiratoryOther', e.target.value)} />
              </div>
            </div>
          </div>

          {/* SKIN Section */}
          <div className="border rounded-lg p-4 bg-yellow-50">
            <h4 className="font-bold text-lg mb-3 text-yellow-800">SKIN</h4>
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-4">
                <VoiceDictationInput label="Edema Location" value={formData.edemaLocation} onChange={(e) => handleInputChange('edemaLocation', e.target.value)} className="w-48" />
                <CheckboxField label="TR" checked={formData.edemaTR} onChange={(v) => handleInputChange('edemaTR', v)} />
                <CheckboxField label="1+" checked={formData.edema1Plus} onChange={(v) => handleInputChange('edema1Plus', v)} />
                <CheckboxField label="2+" checked={formData.edema2Plus} onChange={(v) => handleInputChange('edema2Plus', v)} />
                <CheckboxField label="3+" checked={formData.edema3Plus} onChange={(v) => handleInputChange('edema3Plus', v)} />
                <CheckboxField label="4+" checked={formData.edema4Plus} onChange={(v) => handleInputChange('edema4Plus', v)} />
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <CheckboxField label="Non Pitting" checked={formData.nonPitting} onChange={(v) => handleInputChange('nonPitting', v)} />
                <CheckboxField label="Pitting" checked={formData.pitting} onChange={(v) => handleInputChange('pitting', v)} />
                <CheckboxField label="No Deficit" checked={formData.skinNoDeficit} onChange={(v) => handleInputChange('skinNoDeficit', v)} />
                <CheckboxField label="Warm/Dry" checked={formData.warmDry} onChange={(v) => handleInputChange('warmDry', v)} />
                <CheckboxField label="Cool/Clammy" checked={formData.coolClammy} onChange={(v) => handleInputChange('coolClammy', v)} />
                <CheckboxField label="Turgor Adequate" checked={formData.turgorAdequate} onChange={(v) => handleInputChange('turgorAdequate', v)} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border p-3 rounded">
                  <VoiceDictationInput label="1st Wound Location" value={formData.wound1Location} onChange={(e) => handleInputChange('wound1Location', e.target.value)} />
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <VoiceDictationInput label="L" value={formData.wound1L} onChange={(e) => handleInputChange('wound1L', e.target.value)} />
                    <VoiceDictationInput label="W" value={formData.wound1W} onChange={(e) => handleInputChange('wound1W', e.target.value)} />
                    <VoiceDictationInput label="D" value={formData.wound1D} onChange={(e) => handleInputChange('wound1D', e.target.value)} />
                  </div>
                  <Label className="mt-2 block font-semibold">DRAINAGE</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <VoiceDictationInput label="Amt" value={formData.wound1DrainageAmt} onChange={(e) => handleInputChange('wound1DrainageAmt', e.target.value)} />
                    <VoiceDictationInput label="Color" value={formData.wound1DrainageColor} onChange={(e) => handleInputChange('wound1DrainageColor', e.target.value)} />
                    <VoiceDictationInput label="Odor" value={formData.wound1DrainageOdor} onChange={(e) => handleInputChange('wound1DrainageOdor', e.target.value)} />
                  </div>
                </div>
                <div className="border p-3 rounded">
                  <VoiceDictationInput label="2nd Wound Location" value={formData.wound2Location} onChange={(e) => handleInputChange('wound2Location', e.target.value)} />
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <VoiceDictationInput label="L" value={formData.wound2L} onChange={(e) => handleInputChange('wound2L', e.target.value)} />
                    <VoiceDictationInput label="W" value={formData.wound2W} onChange={(e) => handleInputChange('wound2W', e.target.value)} />
                    <VoiceDictationInput label="D" value={formData.wound2D} onChange={(e) => handleInputChange('wound2D', e.target.value)} />
                  </div>
                  <Label className="mt-2 block font-semibold">DRAINAGE</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <VoiceDictationInput label="Amt" value={formData.wound2DrainageAmt} onChange={(e) => handleInputChange('wound2DrainageAmt', e.target.value)} />
                    <VoiceDictationInput label="Color" value={formData.wound2DrainageColor} onChange={(e) => handleInputChange('wound2DrainageColor', e.target.value)} />
                    <VoiceDictationInput label="Odor" value={formData.wound2DrainageOdor} onChange={(e) => handleInputChange('wound2DrainageOdor', e.target.value)} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* GU Section */}
          <div className="border rounded-lg p-4 bg-red-50">
            <h4 className="font-bold text-lg mb-3 text-red-800">GU</h4>
            <div className="flex flex-wrap items-center gap-4 mb-3">
              <CheckboxField label="No Deficit" checked={formData.guNoDeficit} onChange={(v) => handleInputChange('guNoDeficit', v)} />
              <CheckboxField label="Distention" checked={formData.distention} onChange={(v) => handleInputChange('distention', v)} />
              <CheckboxField label="Retention" checked={formData.retention} onChange={(v) => handleInputChange('retention', v)} />
              <CheckboxField label="Burning" checked={formData.burning} onChange={(v) => handleInputChange('burning', v)} />
              <CheckboxField label="Frequency" checked={formData.frequency} onChange={(v) => handleInputChange('frequency', v)} />
              <CheckboxField label="Foleycath" checked={formData.foleyCath} onChange={(v) => handleInputChange('foleyCath', v)} />
              <CheckboxField label="Suprapubic" checked={formData.suprapubic} onChange={(v) => handleInputChange('suprapubic', v)} />
              <CheckboxField label="Incontinence" checked={formData.incontinence} onChange={(v) => handleInputChange('incontinence', v)} />
            </div>
            <div className="grid grid-cols-4 gap-2">
              <VoiceDictationInput label="Size" value={formData.catheterSize} onChange={(e) => handleInputChange('catheterSize', e.target.value)} />
              <VoiceDictationInput label="F" value={formData.catheterF} onChange={(e) => handleInputChange('catheterF', e.target.value)} />
              <VoiceDictationInput label="ml" value={formData.catheterMl} onChange={(e) => handleInputChange('catheterMl', e.target.value)} />
              <VoiceDictationInput label="Comments:" value={formData.guComments} onChange={(e) => handleInputChange('guComments', e.target.value)} />
            </div>
          </div>

          {/* CARDIOVASCULAR Section */}
          <div className="border rounded-lg p-4 bg-pink-50">
            <h4 className="font-bold text-lg mb-3 text-pink-800">CARDIOVASCULAR</h4>
            <div className="flex flex-wrap items-center gap-4 mb-3">
              <CheckboxField label="No Deficit" checked={formData.cvNoDeficit} onChange={(v) => handleInputChange('cvNoDeficit', v)} />
              <CheckboxField label="Chest Pain" checked={formData.chestPain} onChange={(v) => handleInputChange('chestPain', v)} />
              <CheckboxField label="Heart Sounds" checked={formData.heartSounds} onChange={(v) => handleInputChange('heartSounds', v)} />
              <CheckboxField label="Peripheral Pulses" checked={formData.peripheralPulses} onChange={(v) => handleInputChange('peripheralPulses', v)} />
              <CheckboxField label="Dizziness" checked={formData.dizziness} onChange={(v) => handleInputChange('dizziness', v)} />
              <CheckboxField label="Edema" checked={formData.edema} onChange={(v) => handleInputChange('edema', v)} />
              <CheckboxField label="Neck Vein Distention" checked={formData.neckVeinDistention} onChange={(v) => handleInputChange('neckVeinDistention', v)} />
              <CheckboxField label="Arrhythmia" checked={formData.arrhythmia} onChange={(v) => handleInputChange('arrhythmia', v)} />
            </div>
            <VoiceDictationInput label="Comments:" value={formData.cvComments} onChange={(e) => handleInputChange('cvComments', e.target.value)} />
          </div>

          {/* MUSCULOSKELETAL Section */}
          <div className="border rounded-lg p-4 bg-indigo-50">
            <h4 className="font-bold text-lg mb-3 text-indigo-800">MUSCULOSKELETAL</h4>
            <div className="flex flex-wrap items-center gap-4 mb-3">
              <CheckboxField label="No Deficit" checked={formData.msNoDeficit} onChange={(v) => handleInputChange('msNoDeficit', v)} />
              <CheckboxField label="Weakness" checked={formData.weakness} onChange={(v) => handleInputChange('weakness', v)} />
              <CheckboxField label="Balance/Gait Abnormal" checked={formData.balanceGaitAbnormal} onChange={(v) => handleInputChange('balanceGaitAbnormal', v)} />
              <CheckboxField label="Limited Mobility/ROM" checked={formData.limitedMobilityROM} onChange={(v) => handleInputChange('limitedMobilityROM', v)} />
              <CheckboxField label="Pain" checked={formData.pain} onChange={(v) => handleInputChange('pain', v)} />
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <VoiceDictationInput label="Grip Strength" value={formData.gripStrength} onChange={(e) => handleInputChange('gripStrength', e.target.value)} />
              <VoiceDictationInput label="right" value={formData.gripStrengthRight} onChange={(e) => handleInputChange('gripStrengthRight', e.target.value)} />
              <VoiceDictationInput label="left" value={formData.gripStrengthLeft} onChange={(e) => handleInputChange('gripStrengthLeft', e.target.value)} />
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <CheckboxField label="Bed bound" checked={formData.bedbound} onChange={(v) => handleInputChange('bedbound', v)} />
              <CheckboxField label="Chair bound" checked={formData.chairbound} onChange={(v) => handleInputChange('chairbound', v)} />
              <CheckboxField label="Contracture" checked={formData.contracture} onChange={(v) => handleInputChange('contracture', v)} />
              <CheckboxField label="Paralysis" checked={formData.paralysis} onChange={(v) => handleInputChange('paralysis', v)} />
              <CheckboxField label="Assistive/Device" checked={formData.assistiveDevice} onChange={(v) => handleInputChange('assistiveDevice', v)} />
              <CheckboxField label="Fall Precautions maintained" checked={formData.fallPrecautionsMaintained} onChange={(v) => handleInputChange('fallPrecautionsMaintained', v)} />
            </div>
          </div>

          {/* NEUROLOGICAL Section */}
          <div className="border rounded-lg p-4 bg-teal-50">
            <h4 className="font-bold text-lg mb-3 text-teal-800">NEUROLOGICAL</h4>
            <div className="flex flex-wrap items-center gap-4 mb-3">
              <CheckboxField label="No Deficit" checked={formData.neuroNoDeficit} onChange={(v) => handleInputChange('neuroNoDeficit', v)} />
              <CheckboxField label="Oriented to Person" checked={formData.orientedToPerson} onChange={(v) => handleInputChange('orientedToPerson', v)} />
              <CheckboxField label="Place" checked={formData.orientedToPlace} onChange={(v) => handleInputChange('orientedToPlace', v)} />
              <CheckboxField label="Time" checked={formData.orientedToTime} onChange={(v) => handleInputChange('orientedToTime', v)} />
              <CheckboxField label="Seizure/Tremors" checked={formData.seizureTremors} onChange={(v) => handleInputChange('seizureTremors', v)} />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <VoiceDictationInput label="Pupillary Reaction" value={formData.pupillaryReaction} onChange={(e) => handleInputChange('pupillaryReaction', e.target.value)} />
              <VoiceDictationInput label="Right/Left/Equal" value={formData.pupillaryRightLeftEqual} onChange={(e) => handleInputChange('pupillaryRightLeftEqual', e.target.value)} />
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <CheckboxField label="Hearing Impaired" checked={formData.hearingImpaired} onChange={(v) => handleInputChange('hearingImpaired', v)} />
              <CheckboxField label="Speech Impaired" checked={formData.speechImpaired} onChange={(v) => handleInputChange('speechImpaired', v)} />
              <CheckboxField label="Visually Impaired" checked={formData.visuallyImpaired} onChange={(v) => handleInputChange('visuallyImpaired', v)} />
              <CheckboxField label="Legally Blind" checked={formData.legallyBlind} onChange={(v) => handleInputChange('legallyBlind', v)} />
            </div>
          </div>

          {/* DIGESTIVE/NUTRITION Section */}
          <div className="border rounded-lg p-4 bg-orange-50">
            <h4 className="font-bold text-lg mb-3 text-orange-800">DIGESTIVE/NUTRITION</h4>
            <div className="flex flex-wrap items-center gap-4 mb-3">
              <CheckboxField label="No Deficit" checked={formData.digestiveNoDeficit} onChange={(v) => handleInputChange('digestiveNoDeficit', v)} />
              <VoiceDictationInput label="Last BM" value={formData.lastBM} onChange={(e) => handleInputChange('lastBM', e.target.value)} className="w-32" />
              <CheckboxField label="N/V" checked={formData.nv} onChange={(v) => handleInputChange('nv', v)} />
              <CheckboxField label="Diarrhea" checked={formData.diarrhea} onChange={(v) => handleInputChange('diarrhea', v)} />
              <CheckboxField label="Constipation" checked={formData.constipation} onChange={(v) => handleInputChange('constipation', v)} />
              <CheckboxField label="Tube Feeding" checked={formData.tubeFeeding} onChange={(v) => handleInputChange('tubeFeeding', v)} />
            </div>
            <div className="grid grid-cols-3 gap-4 mb-3">
              <VoiceDictationInput label="NPO Type/Amount" value={formData.npoTypeAmount} onChange={(e) => handleInputChange('npoTypeAmount', e.target.value)} />
              <div className="flex items-center gap-2">
                <CheckboxField label="Placement" checked={formData.placement} onChange={(v) => handleInputChange('placement', v)} />
              </div>
              <VoiceDictationInput label="Residual/Amt." value={formData.residualAmt} onChange={(e) => handleInputChange('residualAmt', e.target.value)} />
            </div>
            <div className="flex flex-wrap items-center gap-4 mb-3">
              <CheckboxField label="Bowel Sounds Present" checked={formData.bowelSoundsPresent} onChange={(v) => handleInputChange('bowelSoundsPresent', v)} />
              <VoiceDictationInput label="Abd. Girth" value={formData.abdGirth} onChange={(e) => handleInputChange('abdGirth', e.target.value)} className="w-32" />
              <VoiceDictationInput label="Diet" value={formData.diet} onChange={(e) => handleInputChange('diet', e.target.value)} className="w-48" />
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <CheckboxField label="Meals Prepared & Administered" checked={formData.mealsPreparedAdministered} onChange={(v) => handleInputChange('mealsPreparedAdministered', v)} />
              <CheckboxField label="Appropriately" checked={formData.appropriately} onChange={(v) => handleInputChange('appropriately', v)} />
              <span className="text-sm">Past 24-Hour Diet Recall:</span>
              <CheckboxField label="Adequate" checked={formData.past24HourDietRecallAdequate} onChange={(v) => handleInputChange('past24HourDietRecallAdequate', v)} />
              <CheckboxField label="Inadequate" checked={formData.past24HourDietRecallInadequate} onChange={(v) => handleInputChange('past24HourDietRecallInadequate', v)} />
            </div>
          </div>

          {/* PAIN Section */}
          <div className="border rounded-lg p-4 bg-red-100">
            <h4 className="font-bold text-lg mb-3 text-red-800">PAIN</h4>
            <div className="space-y-4">
              <div>
                <Label className="font-semibold">Frequency of Pain interfering with patient's activity or movement:</Label>
                <div className="flex flex-wrap items-center gap-4 mt-2">
                  <CheckboxField label="0 - Patient has none or pain doesn't interfere with activity or movement" checked={formData.painFrequency0} onChange={(v) => handleInputChange('painFrequency0', v)} />
                  <CheckboxField label="1 - Less than daily" checked={formData.painFrequency1} onChange={(v) => handleInputChange('painFrequency1', v)} />
                  <CheckboxField label="2 - Daily, but not constantly" checked={formData.painFrequency2} onChange={(v) => handleInputChange('painFrequency2', v)} />
                  <CheckboxField label="3 - All of the time" checked={formData.painFrequency3} onChange={(v) => handleInputChange('painFrequency3', v)} />
                </div>
              </div>
              <div className="border p-3 rounded bg-white">
                <Label className="font-bold">PAIN PROFILE</Label>
                <VoiceDictationInput label="Primary Site:" value={formData.painPrimarySite} onChange={(e) => handleInputChange('painPrimarySite', e.target.value)} className="mt-2" />
                <div className="mt-3">
                  <Label>Intensity: (0-10 scale)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs">low</span>
                    {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
                      <CheckboxField key={n} label={n.toString()} checked={formData[`painIntensity${n}`]} onChange={(v) => handleInputChange(`painIntensity${n}`, v)} />
                    ))}
                    <span className="text-xs">high</span>
                  </div>
                </div>
              </div>
              <VoiceDictationInput label="Current pain management & effectiveness:" value={formData.currentPainManagement} onChange={(e) => handleInputChange('currentPainManagement', e.target.value)} rows={2} />
              <VoiceDictationInput label="Pain Management Teaching to patient/family" value={formData.painManagementTeaching} onChange={(e) => handleInputChange('painManagementTeaching', e.target.value)} rows={2} />
              <VoiceDictationInput label="Patient's pain goal:" value={formData.patientsPainGoal} onChange={(e) => handleInputChange('patientsPainGoal', e.target.value)} />
              <VoiceDictationInput label="Progress toward pain goal:" value={formData.progressTowardPainGoal} onChange={(e) => handleInputChange('progressTowardPainGoal', e.target.value)} rows={2} />
            </div>
          </div>

          {/* DIAGNOSIS ICD-10 CODES */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <h4 className="font-bold text-lg mb-3">DIAGNOSIS ICD-10 CODES</h4>
            <VoiceDictationInput value={formData.diagnosisIcd10Codes} onChange={(e) => handleInputChange('diagnosisIcd10Codes', e.target.value)} rows={3} />
          </div>

          {/* SUPERVISION Section */}
          <div className="border rounded-lg p-4 bg-cyan-50">
            <h4 className="font-bold text-lg mb-3 text-cyan-800">SUPERVISION</h4>
            <div className="flex flex-wrap items-center gap-4 mb-3">
              <CheckboxField label="LPN" checked={formData.supervisionLPN} onChange={(v) => handleInputChange('supervisionLPN', v)} />
              <CheckboxField label="Aide" checked={formData.supervisionAide} onChange={(v) => handleInputChange('supervisionAide', v)} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-sm">Present on this visit?</Label>
                <div className="flex gap-2 mt-1">
                  <CheckboxField label="Yes" checked={formData.presentOnThisVisitYes} onChange={(v) => handleInputChange('presentOnThisVisitYes', v)} />
                  <CheckboxField label="No" checked={formData.presentOnThisVisitNo} onChange={(v) => handleInputChange('presentOnThisVisitNo', v)} />
                </div>
              </div>
              <div>
                <Label className="text-sm">Aide following care plan?</Label>
                <div className="flex gap-2 mt-1">
                  <CheckboxField label="Yes" checked={formData.aideFollowingCarePlanYes} onChange={(v) => handleInputChange('aideFollowingCarePlanYes', v)} />
                  <CheckboxField label="No" checked={formData.aideFollowingCarePlanNo} onChange={(v) => handleInputChange('aideFollowingCarePlanNo', v)} />
                </div>
              </div>
              <div>
                <Label className="text-sm">Courteous and polite?</Label>
                <div className="flex gap-2 mt-1">
                  <CheckboxField label="Yes" checked={formData.courteousAndPoliteYes} onChange={(v) => handleInputChange('courteousAndPoliteYes', v)} />
                  <CheckboxField label="No" checked={formData.courteousAndPoliteNo} onChange={(v) => handleInputChange('courteousAndPoliteNo', v)} />
                </div>
              </div>
              <div>
                <Label className="text-sm">Report changes in status?</Label>
                <div className="flex gap-2 mt-1">
                  <CheckboxField label="Yes" checked={formData.reportChangesInStatusYes} onChange={(v) => handleInputChange('reportChangesInStatusYes', v)} />
                  <CheckboxField label="No" checked={formData.reportChangesInStatusNo} onChange={(v) => handleInputChange('reportChangesInStatusNo', v)} />
                </div>
              </div>
              <div>
                <Label className="text-sm">Patient satisfied with care?</Label>
                <div className="flex gap-2 mt-1">
                  <CheckboxField label="Yes" checked={formData.patientSatisfiedWithCareYes} onChange={(v) => handleInputChange('patientSatisfiedWithCareYes', v)} />
                  <CheckboxField label="No" checked={formData.patientSatisfiedWithCareNo} onChange={(v) => handleInputChange('patientSatisfiedWithCareNo', v)} />
                </div>
              </div>
              <div>
                <Label className="text-sm">Changes made to care plan?</Label>
                <div className="flex gap-2 mt-1">
                  <CheckboxField label="Yes" checked={formData.changesMadeToCarePlanYes} onChange={(v) => handleInputChange('changesMadeToCarePlanYes', v)} />
                  <CheckboxField label="No" checked={formData.changesMadeToCarePlanNo} onChange={(v) => handleInputChange('changesMadeToCarePlanNo', v)} />
                </div>
              </div>
              <div>
                <Label className="text-sm">Additional instruction given?</Label>
                <div className="flex gap-2 mt-1">
                  <CheckboxField label="Yes" checked={formData.additionalInstructionGivenYes} onChange={(v) => handleInputChange('additionalInstructionGivenYes', v)} />
                  <CheckboxField label="No" checked={formData.additionalInstructionGivenNo} onChange={(v) => handleInputChange('additionalInstructionGivenNo', v)} />
                </div>
              </div>
            </div>
          </div>

          {/* GOALS/PLANS Section */}
          <div className="border rounded-lg p-4 bg-lime-50">
            <h4 className="font-bold text-lg mb-3 text-lime-800">GOALS/PLANS</h4>
            <VoiceDictationInput value={formData.goalsPlans} onChange={(e) => handleInputChange('goalsPlans', e.target.value)} rows={3} />
          </div>

          {/* PROGRESS/BARRIERS Section */}
          <div className="border rounded-lg p-4 bg-amber-50">
            <h4 className="font-bold text-lg mb-3 text-amber-800">PROGRESS/BARRIERS</h4>
            <VoiceDictationInput value={formData.progressBarriers} onChange={(e) => handleInputChange('progressBarriers', e.target.value)} rows={3} />
          </div>

          {/* INTERVENTIONS Section */}
          <div className="border rounded-lg p-4 bg-emerald-50">
            <h4 className="font-bold text-lg mb-3 text-emerald-800">INTERVENTIONS</h4>
            <div className="space-y-4">
              <VoiceDictationInput label="Progress toward goals:" value={formData.progressTowardGoals} onChange={(e) => handleInputChange('progressTowardGoals', e.target.value)} rows={2} />
              <VoiceDictationInput label="Teaching Tools used/given:" value={formData.teachingToolsUsedGiven} onChange={(e) => handleInputChange('teachingToolsUsedGiven', e.target.value)} />
              <div className="flex flex-wrap items-center gap-4">
                <CheckboxField label="Instructed" checked={formData.instructed} onChange={(v) => handleInputChange('instructed', v)} />
                <CheckboxField label="Pt/Cg. Verbalized Understanding" checked={formData.ptCgVerbalizedUnderstanding} onChange={(v) => handleInputChange('ptCgVerbalizedUnderstanding', v)} />
                <CheckboxField label="Pt/Cg. Return Demonstration" checked={formData.ptCgReturnDemonstration} onChange={(v) => handleInputChange('ptCgReturnDemonstration', v)} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <VoiceDictationInput label="Conference with: SN PT OT SLP MSS HHA (circle one) Name:" value={formData.conferenceWithName} onChange={(e) => handleInputChange('conferenceWithName', e.target.value)} />
                <VoiceDictationInput label="Regarding:" value={formData.regarding} onChange={(e) => handleInputChange('regarding', e.target.value)} />
              </div>
            </div>
          </div>

          {/* CASE MANAGEMENT Section */}
          <div className="border rounded-lg p-4 bg-violet-50">
            <h4 className="font-bold text-lg mb-3 text-violet-800">CASE MANAGEMENT (ADDITIONAL INFORMATION)</h4>
            <VoiceDictationInput 
              label="CONFERENCE WITH: (e.g. Conference with: MD OR MD'S OFFICE.):" 
              value={formData.caseManagementConferenceWith} 
              onChange={(e) => handleInputChange('caseManagementConferenceWith', e.target.value)} 
              rows={3} 
            />
          </div>

          {/* Signatures - Comprehensive with Type/Voice Recording/Handwritten */}
          <div className="border rounded-lg p-4 bg-gray-100">
            <h4 className="font-bold text-lg mb-3">SIGNATURES</h4>
            <p className="text-sm text-gray-600 mb-4">Choose your preferred signature method: Type, Voice Recording, or Handwritten</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-3">
                <SignatureInput 
                  label="Patient's Signature" 
                  value={formData.patientSignature} 
                  onChange={(value) => handleInputChange('patientSignature', value)} 
                />
                <VoiceDictationInput label="Date" value={formData.patientSignatureDate} onChange={(e) => handleInputChange('patientSignatureDate', e.target.value)} placeholder="MM/DD/YYYY" />
              </div>
              <div className="space-y-3">
                <SignatureInput 
                  label="Nurse Signature" 
                  value={formData.nurseSignature} 
                  onChange={(value) => handleInputChange('nurseSignature', value)} 
                />
                <VoiceDictationInput label="Date" value={formData.nurseSignatureDate} onChange={(e) => handleInputChange('nurseSignatureDate', e.target.value)} placeholder="MM/DD/YYYY" />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#f39c12] hover:bg-[#e67e22]">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Form'}
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ComprehensiveNursesNotes;
